import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Zap, Shield, Clock, Users, TrendingUp, Award } from 'lucide-react';
import { supabase } from '../supabaseClient';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch featured products
      const { data: products } = await supabase
        .from('products')
        .select('*')
        .eq('is_featured', true)
        .limit(6);
      
      setFeaturedProducts(products || []);

      // Fetch categories
      const { data: cats } = await supabase
        .from('categories')
        .select('*')
        .order('created_at', { ascending: true });
      
      setCategories(cats || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const testimonials = [
    { 
      name: 'Rahul Sharma', 
      role: 'Startup Founder', 
      text: 'Saved us 3 months of development time. The code quality is exceptional and documentation is crystal clear!',
      avatar: 'RS'
    },
    { 
      name: 'Priya Verma', 
      role: 'CTO', 
      text: 'Production-ready code with excellent documentation. Best investment for our team. Highly recommended!',
      avatar: 'PV'
    },
    { 
      name: 'Amit Patel', 
      role: 'Developer', 
      text: 'Best investment for our project. Clean architecture, well-documented, and easy to customize.',
      avatar: 'AP'
    },
  ];

  return (
    <div className="space-y-32">
      {/* Hero Section - Already Good */}
      <section className="hero-gradient py-24 pt-32 relative overflow-hidden">
        <div className="section-container">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 animate-pulse-slow">
              <Zap size={16} className="text-cyan-400" />
              <span>Production-Ready Code for Modern Developers</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold font-poppins leading-tight">
              Build Faster with <br />
              <span className="gradient-text">Script Saint</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Buy prebuilt, well-documented, and deployable websites. Choose from Next.js, React, Flask, Django, Node - get source code, CI/CD examples, and deployment support.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center pt-6">
              <Link to="/shop" className="gradient-button inline-flex items-center justify-center space-x-2 text-lg px-10 py-5 rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50" data-testid="browse-products-hero">
                <span>Browse Products</span>
                <ArrowRight size={22} />
              </Link>
              <Link to="/portfolio" className="glass-card inline-flex items-center justify-center space-x-2 px-10 py-5 rounded-xl text-gray-300 hover:text-white hover:border-cyan-500/30 transition-all">
                <span>View Portfolio</span>
              </Link>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-8 pt-8 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>Production Ready</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>Full Documentation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>Lifetime Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Why Choose Us - Enhanced */}
      <section className="section-container relative">
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
            <Award size={16} className="text-cyan-400" />
            <span>Why Developers Choose Us</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-6">
            Why Choose <span className="gradient-text">Script Saint</span>?
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            We deliver production-grade code with clean architecture, comprehensive documentation, and deployment support.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="glass-card p-10 text-center card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                <Zap className="text-gray-900" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">Lightning Fast</h3>
              <p className="text-gray-400 leading-relaxed">Skip months of development. Get production-ready code instantly and deploy faster than ever.</p>
            </div>
          </div>
          
          <div className="glass-card p-10 text-center card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                <Shield className="text-gray-900" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">Battle Tested</h3>
              <p className="text-gray-400 leading-relaxed">Production-grade code with best practices, security standards, and performance optimizations built-in.</p>
            </div>
          </div>
          
          <div className="glass-card p-10 text-center card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl shadow-cyan-500/30">
                <Clock className="text-gray-900" size={40} />
              </div>
              <h3 className="text-2xl font-bold mb-4 group-hover:text-cyan-400 transition-colors">Full Support</h3>
              <p className="text-gray-400 leading-relaxed">Complete documentation, CI/CD examples, and optional white-glove deployment service available.</p>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
            <div className="text-4xl font-bold gradient-text mb-2">500+</div>
            <div className="text-gray-400 text-sm">Projects Delivered</div>
          </div>
          <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
            <div className="text-4xl font-bold gradient-text mb-2">1000+</div>
            <div className="text-gray-400 text-sm">Happy Developers</div>
          </div>
          <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
            <div className="text-4xl font-bold gradient-text mb-2">50+</div>
            <div className="text-gray-400 text-sm">Products</div>
          </div>
          <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
            <div className="text-4xl font-bold gradient-text mb-2">99%</div>
            <div className="text-gray-400 text-sm">Satisfaction</div>
          </div>
        </div>
      </section>

      {/* Categories - Enhanced */}
      <section className="section-container relative">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
            <TrendingUp size={16} className="text-cyan-400" />
            <span>Popular Categories</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-6">
            Explore <span className="gradient-text">Categories</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            From trading platforms to AI tools - find the perfect solution for your project
          </p>
        </div>
        
        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-lg">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center text-gray-400 py-20">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <p className="text-xl">No categories available yet.</p>
              <p className="text-sm text-gray-500 mt-2">Admin can add them from the dashboard.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {categories.map((category) => (
              <CategoryCard 
                key={category.id} 
                category={category} 
                isHighlighted={category.name.toLowerCase().includes('trading')}
              />
            ))}
          </div>
        )}
      </section>

      {/* Featured Products - Enhanced */}
      <section className="section-container relative">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-20">
          <div>
            <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
              <Zap size={16} className="text-cyan-400" />
              <span>Handpicked for You</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-4">
              Featured <span className="gradient-text">Products</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl">Hand-picked production-ready solutions for your business</p>
          </div>
          <Link to="/shop" className="hidden md:flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition mt-4 md:mt-0 px-6 py-3 glass-card rounded-xl hover:border-cyan-500/30" data-testid="view-all-products">
            <span className="font-semibold">View All</span>
            <ArrowRight size={20} />
          </Link>
        </div>
        
        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-lg">Loading products...</p>
          </div>
        ) : featuredProducts.length === 0 ? (
          <div className="text-center text-gray-400 py-20">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <p className="text-xl">No featured products yet.</p>
              <p className="text-sm text-gray-500 mt-2">Admin can add them from the dashboard.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} featured />
            ))}
          </div>
        )}
        
        <div className="mt-12 text-center md:hidden">
          <Link to="/shop" className="gradient-button inline-flex items-center space-x-2 px-8 py-4 rounded-xl shadow-lg shadow-cyan-500/30">
            <span>View All Products</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Testimonials - Enhanced */}
      <section className="section-container relative">
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
        
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
            <Users size={16} className="text-cyan-400" />
            <span>Trusted by Developers</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-6">
            What Developers <span className="gradient-text">Say</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Trusted by startups and agencies worldwide
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="glass-card p-8 card-hover group relative overflow-hidden rounded-2xl border border-cyan-500/10">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                {/* Avatar */}
                <div className="w-16 h-16 mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center text-gray-900 font-bold text-lg shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {testimonial.avatar}
                </div>
                
                {/* Stars */}
                <div className="flex items-center space-x-1 text-yellow-400 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-lg">★</span>
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-gray-300 mb-6 leading-relaxed text-base">"{testimonial.text}"</p>
                
                {/* Author */}
                <div className="pt-4 border-t border-gray-800/50">
                  <div className="font-semibold text-white text-lg group-hover:text-cyan-400 transition-colors">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="section-container">
        <div className="glass-card p-16 md:p-20 text-center rounded-3xl border-2 border-cyan-500/20 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
              <Zap size={16} className="text-cyan-400" />
              <span>Start Building Today</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold font-poppins mb-6">
              Ready to <span className="gradient-text">Build Faster</span>?
            </h2>
            <p className="text-gray-300 text-xl mb-10 max-w-3xl mx-auto leading-relaxed">
              Join hundreds of developers who are shipping products faster with our production-ready code.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link to="/shop" className="gradient-button inline-flex items-center justify-center space-x-2 text-lg px-10 py-5 rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50" data-testid="browse-all-products-cta">
                <span>Browse All Products</span>
                <ArrowRight size={22} />
              </Link>
              <Link to="/contact" className="glass-card inline-flex items-center justify-center px-10 py-5 rounded-xl text-gray-300 hover:text-white hover:border-cyan-500/30 transition-all">
                <span>Request Custom Project</span>
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>No Hidden Fees</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="text-cyan-400" size={20} />
                <span>Money-Back Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;